import React from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

/**
 * The measurement sets taken with the Spectre Motion sensor app for one
 * bowler. Same chrome as the PAP History modal (ModalPAPAudit): navy stripe,
 * surfaceAlt info card, table, light Close.
 */
const StyledModal = styled(Modal)`
  @media (min-width: 992px) {
    .modal-lg,
    .modal-xl {
      max-width: 640px;
    }
  }

  .modal-content {
    background-color: #ffffff;
    border: none;
    border-radius: ${(p) => p.theme.radius.md};
    box-shadow:
      inset 0 4px 0 ${(p) => p.theme.color.primary},
      0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  }

  .modal-header {
    border-bottom: 1px solid ${(p) => p.theme.color.border};
    padding: calc(${(p) => p.theme.spacing.md} + 4px) ${(p) => p.theme.spacing.md} ${(p) => p.theme.spacing.md};
  }

  .modal-body {
    min-height: 30vh;
    max-height: 70vh;
    overflow-y: auto;
    padding: ${(p) => p.theme.spacing.md};
  }

  .modal-footer {
    border-top: 1px solid ${(p) => p.theme.color.border};
    padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  }
`;

const TitleBar = styled.div`
  color: ${(p) => p.theme.color.primary};
  font-family: ${(p) => p.theme.font.family};
  font-weight: ${(p) => p.theme.font.weightBold};
  font-size: ${(p) => p.theme.font.sizeLg};
  line-height: 1.2;
`;

const InfoCard = styled.div`
  background: ${(p) => p.theme.color.surfaceAlt};
  border-radius: ${(p) => p.theme.radius.md};
  padding: ${(p) => p.theme.spacing.md};
  text-align: center;
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};
  color: ${(p) => p.theme.color.text};
  margin-bottom: ${(p) => p.theme.spacing.md};
`;

const TableScroll = styled.div`
  border: 1px solid ${(p) => p.theme.color.border};
  border-radius: ${(p) => p.theme.radius.md};
  overflow: hidden;
`;

const RecordsTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};
  color: ${(p) => p.theme.color.text};

  thead th {
    background: ${(p) => p.theme.color.surfaceAlt};
    color: ${(p) => p.theme.color.primary};
    font-weight: ${(p) => p.theme.font.weightSemibold};
    text-align: left;
    padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
    border-bottom: 1px solid ${(p) => p.theme.color.border};
    white-space: nowrap;
  }

  tbody td {
    padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
    border-bottom: 1px solid ${(p) => p.theme.color.border};
    white-space: nowrap;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const Applied = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(p) => p.theme.color.successBg};
  color: ${(p) => p.theme.color.success};
  font-size: 11px;
  font-weight: ${(p) => p.theme.font.weightSemibold};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${(p) => p.theme.spacing.lg} 0;
  color: ${(p) => p.theme.color.textMuted};
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};
`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};
const deg = (v) => (v == null ? "—" : `${Math.round(v)}°`);
const pap = (m) => {
  if (m.papHIn == null) return "—";
  const p = m.proposed || {};
  const strings = p.pap_h ? `${p.pap_h} × ${p.pap_v || "0"} ${p.pap_ud === "Up" ? "▲" : p.pap_ud === "Down" ? "▼" : ""}` : null;
  return strings || `${m.papHIn.toFixed(2)} × ${(m.papVIn || 0).toFixed(2)} ${m.papDirection || ""}`;
};

const ModalMotionMeasurements = ({ show, onHide, measurements }) => {
  const { t } = useTranslation();
  const rows = measurements || [];
  return (
    <StyledModal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <TitleBar>{t("Motion measurements")}</TitleBar>
      </Modal.Header>
      <Modal.Body>
        <InfoCard>
          {rows.length === 0
            ? t("No measurements yet")
            : `${rows.length} ${rows.length === 1 ? t("measurement") : t("measurements")} · ${t("Measured with the Spectre Motion sensor. Applied sets changed the bowler's PAP, rotation and tilt.")}`}
        </InfoCard>
        {rows.length === 0 ? (
          <EmptyState>{t("No measurements yet")}</EmptyState>
        ) : (
          <TableScroll>
            <RecordsTable>
              <thead>
                <tr>
                  <th>{t("Date")}</th>
                  <th>{t("Shots")}</th>
                  <th>{t("PAP")}</th>
                  <th>{t("Rotation")}</th>
                  <th>{t("Tilt")}</th>
                  <th>{t("Rev Rate")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.id} title={m.fittingBall ? `${t("Fitting ball")}: ${m.fittingBall}` : undefined}>
                    <td>{fmtDate(m.createdAt)}</td>
                    <td>{`${m.keptCount}/${m.shotCount}`}</td>
                    <td>{pap(m)}</td>
                    <td>{m.proposed && m.proposed.axis_rot ? `${m.proposed.axis_rot} · ${deg(m.axisRotationDeg)}` : deg(m.axisRotationDeg)}</td>
                    <td>{m.proposed && m.proposed.axis_tilt ? `${m.proposed.axis_tilt} · ${deg(m.axisTiltDeg)}` : deg(m.axisTiltDeg)}</td>
                    <td>{m.revRateRpm == null ? "—" : `${Math.round(m.revRateRpm)} rpm`}</td>
                    <td>{m.appliedToCustomer ? <Applied>{t("Applied")}</Applied> : null}</td>
                  </tr>
                ))}
              </tbody>
            </RecordsTable>
          </TableScroll>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide}>
          {t("Close")}
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
};

export default ModalMotionMeasurements;
